import React, { useEffect, useState } from 'react';
import { Sparkles, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { getCommsIntelGuestInsights } from '../../../data/api';
import { shortDate } from './ciUtils';
import './miloInsights.css';

// Right-column Milo Insights for the selected guest.
// ← GET /api/communications/intel/guest/{id}/insights
// Renders: sentiment, engagement gauge, and the attendance card which shows
// EITHER a "Confirmed — RSVP'd Yes" badge OR Attendance Likelihood + Confidence
// + signals, exactly as the prototype specifies. Graceful loading + fallback.

const SENTIMENT = {
  positive: { emoji: '😊', label: 'Positive', sub: 'High excitement detected', color: 'var(--ci-green)' },
  neutral: { emoji: '😐', label: 'Neutral', sub: 'Engaged, no strong signal', color: 'var(--ci-txt)' },
  needs_attention: { emoji: '⚠️', label: 'Needs Attention', sub: 'Concern detected in their message', color: 'var(--ci-gold-hi)' },
};

function Gauge({ value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      className="ci-gauge"
      style={{ background: `conic-gradient(var(--ci-gold) 0 ${v}%, rgba(255,255,255,.08) ${v}% 100%)` }}
    >
      <span>{v}%</span>
    </div>
  );
}

function strengthLabel(score) {
  if (score >= 80) return 'Very Strong';
  if (score >= 55) return 'Strong';
  if (score >= 30) return 'Building';
  return 'Early';
}

// "+20" / "+5" — points are always non-negative weights here.
function fmtPts(points) {
  const n = Number(points) || 0;
  return `+${n}`;
}

// Quiet, on-brand disclosure used by the Engagement and Attendance cards.
// `label` is the closed-state affordance; children render when open.
function Explain({ label, open, onToggle, children }) {
  return (
    <>
      <button
        type="button"
        className="ci-expl-toggle"
        onClick={onToggle}
        aria-expanded={open}
        title={open ? 'Hide the breakdown' : label}
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {label}
      </button>
      {open && <div className="ci-expl">{children}</div>}
    </>
  );
}

// One point-by-point row: ✓/✗ · label · +pts. Drives both the engagement
// factors list and the inferred-attendance breakdown (same {label, met, points}).
function ExplainRow({ label, met, points }) {
  return (
    <li className={met ? 'ci-yes' : 'ci-no'}>
      <span className="ci-expl-mark">{met ? '✓' : '✗'}</span>
      <span className="ci-expl-label">{label}</span>
      {(points || points === 0) && <span className="ci-expl-pts">{fmtPts(points)}</span>}
    </li>
  );
}

export default function MiloInsights({ guestId, guestName, onGenerateReply, generating = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [showEngWhy, setShowEngWhy] = useState(false);
  const [showAttWhy, setShowAttWhy] = useState(false);

  useEffect(() => {
    if (!guestId) { setData(null); return undefined; }
    let alive = true;
    setLoading(true);
    setErr(false);
    setShowEngWhy(false);
    setShowAttWhy(false);
    getCommsIntelGuestInsights(guestId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setErr(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [guestId]);

  if (!guestId) {
    return (
      <div className="ci-insights">
        <div className="ci-ins-h"><Sparkles size={15} /> Milo Insights</div>
        <div className="ci-empty">Select a conversation to see Milo's read on that guest.</div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="ci-insights">
        <div className="ci-ins-h"><Sparkles size={15} /> Milo Insights</div>
        <div className="ci-skel" style={{ height: 70 }} />
        <div className="ci-skel" style={{ height: 70 }} />
        <div className="ci-skel" style={{ height: 120 }} />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="ci-insights">
        <div className="ci-ins-h"><Sparkles size={15} /> Milo Insights</div>
        <div className="ci-empty">Insights are unavailable for this guest right now.</div>
      </div>
    );
  }

  const sent = SENTIMENT[data.sentiment] || SENTIMENT.neutral;
  const al = data.attendance_likelihood || {};
  const confirmed = !!al.confirmed || al.basis_kind === 'confirmed_rsvp';
  const declined = al.basis_kind === 'declined';
  const lowConf = !confirmed && !declined && Number(al.confidence_pct || 0) < 55;
  const score = Number(data.engagement_score || 0);

  // Explainability data (additive new fields, with graceful fallbacks).
  const factors = Array.isArray(data.engagement_factors) ? data.engagement_factors : [];
  const metCount = factors.filter((f) => f.met).length;
  const attBreakdown = Array.isArray(al.breakdown) ? al.breakdown : [];
  const attBasis = Array.isArray(al.basis) ? al.basis : [];
  const guestSubject = (guestName && String(guestName).trim()) || data.guest_name || 'this guest';
  // Confidence in the inferred case = 25 + 15 per distinct signal present
  // (floor 20 / ceiling 80). Derive the signal count from the breakdown so the
  // footnote matches the number exactly.
  const attSignalCount = attBreakdown.filter(
    (b) => b.met && !/base likelihood/i.test(String(b.label || '')),
  ).length;

  return (
    <div className="ci-insights">
      <div className="ci-ins-h"><Sparkles size={15} /> Milo Insights</div>
      <div className="ci-ins-subject">Milo's read on <b>{guestSubject}</b></div>

      {/* sentiment */}
      <div className="ci-ins-card">
        <div className="ci-l">Guest Sentiment</div>
        <div className="ci-ins-row">
          <span className="ci-emoji">{sent.emoji}</span>
          <div>
            <div className="ci-big" style={{ color: sent.color }}>{sent.label}</div>
            <div className="ci-smt">{sent.sub}</div>
          </div>
        </div>
        <div className="ci-smt" style={{ marginTop: 6, opacity: 0.8 }}>Estimated from keywords in this guest's messages — a quick read, not a verified mood.</div>
      </div>

      {/* engagement / relationship strength */}
      <div className="ci-ins-card">
        <div className="ci-l">Engagement Strength</div>
        <div className="ci-ins-row">
          <Gauge value={score} />
          <div>
            <div className="ci-big">{strengthLabel(score)}</div>
            <div className="ci-smt">{factors.length ? `${metCount} of ${factors.length} signals met` : ''}</div>
          </div>
        </div>
        {factors.length > 0 && (
          <Explain
            label="What this is based on"
            open={showEngWhy}
            onToggle={() => setShowEngWhy((v) => !v)}
          >
            <ul className="ci-expl-list">
              {factors.map((f) => (
                <ExplainRow key={f.key || f.label} label={f.label} met={f.met} points={f.points} />
              ))}
            </ul>
            <div className="ci-expl-total">
              <span>Engagement score</span>
              <b>{score}/100</b>
            </div>
          </Explain>
        )}
      </div>

      {/* attendance prediction: Confirmed OR Declined OR likelihood+confidence */}
      <div className="ci-ins-card">
        <div className="ci-l">Attendance Prediction</div>

        {confirmed && (
          <div>
            <div className="ci-confirmed-badge"><span className="ci-ck"><CheckCircle2 size={13} /></span> Confirmed — RSVP'd Yes</div>
            <div className="ci-smt">This guest has confirmed. No prediction needed.</div>
            <Explain
              label="Why this number"
              open={showAttWhy}
              onToggle={() => setShowAttWhy((v) => !v)}
            >
              <p className="ci-expl-note">
                <b>Confirmed</b> — this guest RSVP'd Yes, so attendance is certain
                (<b>100%</b>). {al.note ? al.note + ' ' : ''}Confidence is shown as
                <b> 95%</b> (capped — even confirmed plans can still change).
              </p>
            </Explain>
          </div>
        )}

        {declined && (
          <div>
            <div className="ci-confirmed-badge"><span className="ci-ck"><CheckCircle2 size={13} /></span> Declined — RSVP'd No</div>
            <div className="ci-smt">This guest declined. They are not expected to attend.</div>
            <Explain
              label="Why this number"
              open={showAttWhy}
              onToggle={() => setShowAttWhy((v) => !v)}
            >
              <p className="ci-expl-note">
                <b>Declined</b> — this guest sent a 'no' RSVP, so attendance is taken
                as a fact (<b>0%</b>), not inferred from engagement.
                {al.note ? ' ' + al.note : ''}
              </p>
            </Explain>
          </div>
        )}

        {!confirmed && !declined && (
          <div>
            <div className="ci-pred-metrics">
              <div className="ci-pred-m">
                <div className="ci-pl">Attendance</div>
                <div className="ci-pv ci-green">{al.value_pct ?? '—'}%</div>
                <div className="ci-pred-bar"><i className="ci-green" style={{ width: `${al.value_pct || 0}%` }} /></div>
              </div>
              <div className="ci-pred-m">
                <div className="ci-pl">Confidence</div>
                <div className="ci-pv">{al.confidence_pct ?? '—'}%</div>
                <div className="ci-pred-bar"><i style={{ width: `${al.confidence_pct || 0}%` }} /></div>
              </div>
            </div>

            {(attBreakdown.length > 0 || attBasis.length > 0) && (
              <Explain
                label="Why this number"
                open={showAttWhy}
                onToggle={() => setShowAttWhy((v) => !v)}
              >
                <p className="ci-expl-note">
                  No RSVP yet, so attendance is estimated from engagement signals —
                  each adds to the likelihood:
                </p>
                {attBreakdown.length > 0 ? (
                  <>
                    <ul className="ci-expl-list" style={{ marginTop: 9 }}>
                      {attBreakdown.map((b, i) => (
                        <ExplainRow key={b.label || i} label={b.label} met={b.met} points={b.points} />
                      ))}
                    </ul>
                    <div className="ci-expl-total">
                      <span>Attendance likelihood{(al.value_pct ?? 90) >= 90 ? ' (capped at 90%)' : ''}</span>
                      <b>{al.value_pct ?? '—'}%</b>
                    </div>
                  </>
                ) : (
                  // Fallback: backend gave only human strings, not a structured breakdown.
                  <ul className="ci-expl-list" style={{ marginTop: 9 }}>
                    {attBasis.map((b, i) => (
                      <li key={i} className="ci-yes">
                        <span className="ci-expl-mark">✓</span>
                        <span className="ci-expl-label">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="ci-expl-foot">
                  Confidence starts at 25% and rises 15% for each independent signal
                  present ({attSignalCount} so far), with a floor of 20% and a ceiling
                  of 80% — more signals mean a sharper read.
                </p>
              </Explain>
            )}

            {lowConf && (
              <div className="ci-rec-conf ci-low" style={{ marginTop: 9 }}>
                <AlertTriangle size={12} /> Low confidence — limited engagement data so far. The prediction will sharpen as the guest interacts.
              </div>
            )}
          </div>
        )}
      </div>

      {/* suggested reply */}
      <div className="ci-ins-suggest">
        <div className="ci-h"><Sparkles size={14} /> Milo Suggested Reply</div>
        <p>A warm, friendly response acknowledging this guest and confirming any details they asked about.</p>
        <button
          className="ci-btn ci-gold ci-sm"
          style={{ width: '100%' }}
          onClick={onGenerateReply}
          disabled={!onGenerateReply || generating}
          title={onGenerateReply ? 'Milo drafts this in the reply composer' : 'Select a conversation to generate a reply'}
        >
          {generating ? 'Milo is composing…' : 'Generate Reply'}
        </button>
      </div>

      {/* real timeline */}
      <div className="ci-ins-card">
        <div className="ci-l">Guest Timeline</div>
        {Array.isArray(data.timeline) && data.timeline.length > 0 ? (
          <div className="ci-timeline">
            {data.timeline.slice(0, 8).map((ev, i) => (
              <div className="ci-tl" key={i}>
                <span className={`ci-d ${i === 0 ? '' : 'ci-o'}`} />
                <span className="ci-tx">{ev.label}</span>
                <span className="ci-dt">{shortDate(ev.at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="ci-empty" style={{ padding: '8px 0', textAlign: 'left' }}>No recorded activity yet.</div>
        )}
      </div>
    </div>
  );
}
